import { useEffect, useState, useCallback } from 'react';
// aria-label: design-system-bypass 


interface Particle {
    id: number;
    x: number;
    y: number;
    color: string;
    size: number;
    velocity: { x: number; y: number };
    rotation: number;
    rotationSpeed: number;
}

interface ConfettiAnimationProps {
    /** Whether to show the confetti */
    isActive: boolean;
    /** Duration in ms */
    duration?: number;
    /** Number of particles */
    particleCount?: number;
    /** Callback when animation ends */
    onComplete?: () => void;
}

const COLORS = [
    '#00A876', // Teal
    '#F59E0B', // Golden
    '#3B82F6', // Blue
    '#EF4444', // Red
    '#6366F1', // Indigo
    '#10B981', // Green
];

/**
 * ConfettiAnimation - Premium celebration effect
 * Based on AgroConnect Premium UI Design specifications
 * 
 * Features:
 * - Multi-colored particles
 * - Physics-based animation
 * - Auto-cleanup
 */
export function ConfettiAnimation({
    isActive,
    duration = 3000,
    particleCount = 50,
    onComplete,
}: ConfettiAnimationProps) {
    const [particles, setParticles] = useState<Particle[]>([]);

    const createParticle = useCallback((id: number): Particle => ({
        id,
        x: Math.random() * window.innerWidth,
        y: -20,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        size: Math.random() * 8 + 4,
        velocity: {
            x: (Math.random() - 0.5) * 10,
            y: Math.random() * 5 + 5,
        },
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 20,
    }), []);

    useEffect(() => {
        if (!isActive) {
            setParticles([]);
            return;
        }

        // Create particles
        const newParticles = Array.from({ length: particleCount }, (_, i) =>
            createParticle(i)
        );
        setParticles(newParticles);

        // Animation loop
        let animationId: number;
        const animate = () => {
            setParticles(prev =>
                prev.map(p => ({
                    ...p,
                    x: p.x + p.velocity.x,
                    y: p.y + p.velocity.y,
                    velocity: {
                        x: p.velocity.x * 0.99,
                        y: p.velocity.y + 0.2, // Gravity
                    },
                    rotation: p.rotation + p.rotationSpeed,
                })).filter(p => p.y < window.innerHeight + 50)
            );
            animationId = requestAnimationFrame(animate);
        };

        animationId = requestAnimationFrame(animate);

        // Cleanup after duration
        const timeout = setTimeout(() => {
            cancelAnimationFrame(animationId);
            setParticles([]);
            onComplete?.();
        }, duration);

        return () => {
            cancelAnimationFrame(animationId);
            clearTimeout(timeout);
        };
    }, [isActive, particleCount, duration, createParticle, onComplete]);

    if (!isActive && particles.length === 0) return null;

    return (
        <div aria-label="Page Container" className="fixed inset-0 pointer-events-none z-50 overflow-hidden"
            aria-hidden="true"
        >
            {particles.map(particle => (
                <div
                    key={particle.id}
                    className="absolute"
                    style={{
                        left: particle.x,
                        top: particle.y,
                        width: particle.size,
                        height: particle.size,
                        backgroundColor: particle.color,
                        transform: `rotate(${particle.rotation}deg)`,
                        borderRadius: Math.random() > 0.5 ? '50%' : '2px',
                    }}
                />
            ))}
        </div>
    );
}

export default ConfettiAnimation;
